/**
 * cleanupStorage.js
 * 
 * Utility function to clean up all localStorage data after study completion.
 * This ensures user privacy and data hygiene by removing all stored data
 * after successful sync and before Prolific redirect.
 */

import { clearSyncQueue } from './syncQueue';

/**
 * Clear all study-related data from localStorage
 * Called after final sync success to ensure privacy
 */
export function clearAllStudyData() {
  console.log('[CLEANUP] Starting localStorage cleanup...');
  
  // 1. Clear sync queue
  clearSyncQueue();
  
  // 2. User identification and flow
  localStorage.removeItem('userId');
  localStorage.removeItem('currentStep');
  localStorage.removeItem('preferenceStep');
  
  // 3. Movie selection task
  localStorage.removeItem('favoriteMovies');
  localStorage.removeItem('dislikedMovies');
  localStorage.removeItem('movieSelectionStartTime');
  
  // 4. Genre preferences
  localStorage.removeItem('genreStartTime');
  localStorage.removeItem('likedGenres');
  localStorage.removeItem('dislikedGenres');
  localStorage.removeItem('genreRatings');
  localStorage.removeItem('genreShowTable');
  
  // 5. Cast preferences
  localStorage.removeItem('castStartTime');
  localStorage.removeItem('likedPeople');
  localStorage.removeItem('dislikedPeople');
  localStorage.removeItem('peopleRatings');
  localStorage.removeItem('castShowTable');
  
  // 6. Year preferences
  localStorage.removeItem('yearStartTime');
  localStorage.removeItem('yearRatings');
  
  // 7. Runtime preferences
  localStorage.removeItem('runtimeStartTime');
  localStorage.removeItem('runtimePreferences');
  
  // 8. Rating threshold
  localStorage.removeItem('ratingStartTime');
  localStorage.removeItem('ratingThreshold');
  
  // 9. Attribute weights
  localStorage.removeItem('weightsStartTime');
  localStorage.removeItem('attributeWeights');
  
  // 10. Recommendation survey
  localStorage.removeItem('recommendationSurveyStart');
  localStorage.removeItem('recommendationSurveyData');
  localStorage.removeItem('recommendationDone');
  
  // 11. WMT tasks
  localStorage.removeItem('wmtIntroStartTime');
  localStorage.removeItem('wmtPracticeStartTime');
  localStorage.removeItem('wmtTestStartTime');
  
  // 12. Pairwise tasks
  localStorage.removeItem('pairwiseSessionId');
  localStorage.removeItem('pairwiseStartTime');
  localStorage.removeItem('finalComparisonSessionId');
  localStorage.removeItem('finalComparisonStartTime');
  Object.keys(sessionStorage)
    .filter(key => key.startsWith('commonMovieComparisonIntroDismissed:'))
    .forEach(key => sessionStorage.removeItem(key));
  
  // 13. Movie week selection
  localStorage.removeItem('movieWeekStartTime');
  localStorage.removeItem('movieWeekSelections');
  
  // 14. Post surveys
  localStorage.removeItem('postPairwiseSurveyStart');
  localStorage.removeItem('postStudySurveyStart');
  
  // 15. Final preview
  localStorage.removeItem('finalPreviewStartTime');
  
  // 16. Consent
  localStorage.removeItem('consentChecked');
  localStorage.removeItem('consentStartTime');
  
  // 17. Demographics
  localStorage.removeItem('demographicsStartTime');
  
  // 18. Preference intro
  localStorage.removeItem('startTime_preferenceIntro');
  
  console.log('[CLEANUP] ✅ All study data cleared from localStorage');
}

/**
 * Get list of all study-related localStorage keys (for debugging)
 */
export function getStudyDataKeys() {
  const keys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    // Filter out non-study keys (if any)
    if (key && !key.startsWith('_')) {
      keys.push(key);
    }
  }
  return keys;
}
